import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Plus, Check, Wrench, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Service, ServiceCategory, PriceOption, getServices, getServiceCategories } from '../../lib/services';
import { useBusiness } from '../../contexts/BusinessContext';
import CreateServiceModal from '../CreateServiceModal';
import { supabase } from '../../lib/supabase';

interface SelectServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (service: Service, priceOption: any) => Promise<void>;
  bookingId?: string;
  vehicleId?: string;
  showMembershipsTab?: boolean; // Control whether to show memberships tab
  closeOnAdd?: boolean;
}

export default function SelectServiceModal({ 
  isOpen, 
  onClose,
  onAdd,
  bookingId,
  vehicleId,
  showMembershipsTab = true, // Default to true - only hide for online booking editor
  closeOnAdd = true,
}: SelectServiceModalProps) {
  const { selectedBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState<'services' | 'memberships'>('services');
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<Record<string, string>>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Removed all persistence systems
  // No need for duplicate timer systems here

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const loadData = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const [servicesData, categoriesData] = await Promise.all([
        getServices(selectedBusiness.id),
        getServiceCategories(selectedBusiness.id)
      ]);
      
      setServices(servicesData);
      setCategories(categoriesData);

      // Initialize default price options
      const defaultOptions: Record<string, string> = {};
      servicesData.forEach(service => {
        if (service.price_options && service.price_options.length > 0 && service.price_options[0].id) {
          defaultOptions[service.id] = service.price_options[0].id;
        }
      });
      setSelectedPriceOptions(defaultOptions);
      
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadData();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedServices(new Set());
    }
  };

  const handleServiceSelect = async (service: Service, priceOption: any) => {
    try {
      await onAdd(service, priceOption);
      if (closeOnAdd) {
      onClose();
      }
    } catch (err) {
      console.error('Error selecting service:', err);
      setError(err instanceof Error ? err.message : 'Failed to select service');
    }
  };

  // Check if there are any membership services
  const hasMemberships = services.some(svc => svc.is_membership === true) || 
                         categories.some(cat => cat.is_membership === true);

  // Filter categories and services based on active tab
  const filteredByTabCategories = categories.filter(cat => 
    activeTab === 'memberships' 
      ? cat.is_membership === true
      : cat.is_membership !== true
  );

  const filteredByTabServices = services.filter(svc => 
    activeTab === 'memberships' 
      ? svc.is_membership === true
      : svc.is_membership !== true
  );

  // Group services by category
  const servicesByCategory = filteredByTabCategories.map(category => ({
    ...category,
    services: filteredByTabServices.filter(service => service.category_id === category.id)
  })).filter(category => category.services.length > 0);

  // Filter categories and services based on search
  const filteredCategories = servicesByCategory.filter(category => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.services.some(service => 
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      )
    );
  });

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={onClose}
      style={{
        zIndex: 100200,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Select Service
              </h2>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Tab Navigation - Only show if there are memberships AND showMembershipsTab is true */}
            {hasMemberships && showMembershipsTab && (
              <div className="flex gap-2 px-6 border-t border-gray-800">
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-4 py-3 text-[15px] font-semibold transition-all duration-150 border-b-2 touch-manipulation ${
                    activeTab === 'services'
                      ? 'border-blue-500 text-white'
                      : 'border-transparent text-gray-400 active:text-white'
                  }`}
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  Services
                </button>
                <button
                  onClick={() => setActiveTab('memberships')}
                  className={`px-4 py-3 text-[15px] font-semibold transition-all duration-150 border-b-2 touch-manipulation ${
                    activeTab === 'memberships'
                      ? 'border-blue-500 text-white'
                      : 'border-transparent text-gray-400 active:text-white'
                  }`}
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  Memberships
                </button>
              </div>
            )}
          </div>

          {/* Search Section */}
          <div className="bg-white border-b border-gray-200 p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {error && (
              <div className="m-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
                <button
                  onClick={loadData}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading services...</p>
                </div>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery ? 'No matching services' : 'No services available'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add services to get started'}
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Service
                </button>
              </div>
            ) : (
              <div className="space-y-3 mx-6 mt-6">
                {filteredCategories.map((category) => {
                  const isExpanded = expandedCategories.has(category.id);
                  return (
                    <div key={category.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleCategoryExpansion(category.id)}
                        className="w-full px-6 py-4 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <h3 className="text-base font-medium text-gray-900">{category.name}</h3>
                        <div className="flex items-center">
                          <span className="text-sm text-blue-600 mr-2">
                            {category.services.length} service{category.services.length !== 1 ? 's' : ''}
                          </span>
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="divide-y divide-gray-100">
                          {category.services.map((service) => (
                            <div key={service.id} className="p-6">
                              <div className="flex-1">
                                <div className="text-base font-medium text-gray-900 mb-2">{service.name}</div>
                                <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">{service.description}</p>
                                
                                <div className="flex flex-wrap gap-2">
                                  {service.price_options?.map((option) => {
                                    const key = `${service.id}-${option.id}`;
                                    const isSelected = selectedServices.has(key);
                                    
                                    return (
                                      <button
                                        key={option.id}
                                        onClick={() => {
                                          handleServiceSelect(service, option);
                                          setSelectedServices(prev => {
                                            const next = new Set(prev);
                                            next.add(key);
                                            return next;
                                          });
                                        }}
                                        disabled={loading}
                                        className={`inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                                          isSelected 
                                            ? 'bg-green-100 text-green-800 border border-green-200' 
                                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      >
                                        {isSelected ? (
                                          <>
                                            <Check className="h-3.5 w-3.5 mr-1.5" />
                                            Added
                                          </>
                                        ) : (
                                          <>
                                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                                            {option.name} - {(() => {
                                              const isPerUnit = option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft';
                                              const base = isPerUnit ? (option.unit_price || 0) : option.price;
                                              const suffix = option.pricing_type === 'per_unit' ? '/unit' : option.pricing_type === 'per_sqft' ? '/sqft' : '';
                                              const compareAt = (option as any).compare_at_price;
                                              const showCompare = compareAt != null && compareAt > base;
                                              return (
                                                <>
                                                  {showCompare && (
                                                    <span className="line-through opacity-70 mr-1">{`$${Number(compareAt).toLocaleString()}${suffix}`}</span>
                                                  )}
                                                  {`$${base.toLocaleString()}${suffix}`}
                                                </>
                                              );
                                            })()}
                                          </>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create Service Button */}
          <div className="bg-white border-t border-gray-200 p-6">
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="w-full flex items-center justify-center px-4 py-3 text-white font-semibold bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Service
            </button>
          </div>
        </div>
      </div>

      <CreateServiceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={async () => {
          setIsCreateModalOpen(false);
          await loadData();
        }}
        businessId={selectedBusiness?.id}
        categories={filteredByTabCategories}
        onCategoryCreated={loadData}
        isMembership={activeTab === 'memberships'}
        modalTitle={activeTab === 'memberships' ? 'Create Membership' : 'Create Service'}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}